/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * A related-records list for the Account 360 (design reference: the list box
 * in "Customer 360"): a titled card of bordered rows, each a link with a
 * muted second line, an amount on the right and a status badge. Read-only;
 * the page's existing cards keep every add and edit action.
 */

import PropTypes from "prop-types";
import { Link as RouterLink } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { ChevronRight } from "lucide-react";
import { v2 as t } from "styles/tokens/v2";
import { toneFor } from "./format";

export function StatusBadge({ status }) {
  if (!status) return null;
  const tone = toneFor(status);
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        height: 22,
        px: 1,
        borderRadius: "11px",
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: "nowrap",
        bgcolor: tone.bg,
        color: tone.fg,
        textTransform: "capitalize",
      }}
    >
      {String(status).replace(/_/g, " ")}
    </Box>
  );
}
StatusBadge.propTypes = { status: PropTypes.string };
StatusBadge.defaultProps = { status: null };

export default function RelatedList({ title, icon: Icon, accent, rows, loading, emptyText, footer, action }) {
  return (
    <Card sx={{ p: 0, overflow: "hidden" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 2.25, py: 1.75, borderBottom: `1px solid ${t.border}` }}>
        {Icon && (
          <Box sx={{ width: 30, height: 30, borderRadius: "8px", display: "grid", placeItems: "center", bgcolor: accent.tint, color: accent.ink }}>
            <Icon size={16} aria-hidden="true" />
          </Box>
        )}
        <Typography component="h2" sx={{ fontSize: 15, fontWeight: 600, color: t.text, flex: 1 }}>
          {title}
        </Typography>
        {action}
      </Box>

      {loading ? (
        <Box sx={{ p: 2 }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} height={44} />
          ))}
        </Box>
      ) : rows.length === 0 ? (
        <Box sx={{ py: 5, px: 2, textAlign: "center" }}>
          {Icon && (
            <Box sx={{ width: 44, height: 44, mx: "auto", mb: 1.25, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: t.surface2, color: t.text3 }}>
              <Icon size={20} aria-hidden="true" />
            </Box>
          )}
          <Typography sx={{ fontSize: 14, color: t.text2 }}>{emptyText}</Typography>
        </Box>
      ) : (
        <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
          {rows.map((row) => {
            const content = (
              <>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: row.to ? t.link : t.text, whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere", lineHeight: 1.35 }}>
                    {row.primary}
                  </Typography>
                  {row.secondary && (
                    <Typography sx={{ fontSize: 12.5, color: t.text3, mt: 0.25, whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere", lineHeight: 1.35 }}>
                      {row.secondary}
                    </Typography>
                  )}
                </Box>
                {row.amount && (
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: t.text, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                    {row.amount}
                  </Typography>
                )}
                <StatusBadge status={row.status} />
                {row.to && <ChevronRight size={16} color={t.text3} aria-hidden="true" />}
              </>
            );
            const rowSx = {
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 2.25,
              py: 1.5,
              borderBottom: `1px solid ${t.border}`,
              textDecoration: "none",
              "&:hover": row.to ? { bgcolor: t.hover } : {},
              "&:focus-visible": { outline: `2px solid ${t.link}`, outlineOffset: -2 },
            };
            return (
              <li key={row.id} style={{ listStyle: "none" }}>
                {row.to ? (
                  <Box component={RouterLink} to={row.to} sx={rowSx}>
                    {content}
                  </Box>
                ) : (
                  <Box sx={rowSx}>{content}</Box>
                )}
              </li>
            );
          })}
        </Box>
      )}
      {footer && <Box sx={{ px: 2.25, py: 1.25, fontSize: 13, color: t.text2 }}>{footer}</Box>}
    </Card>
  );
}

RelatedList.propTypes = {
  title: PropTypes.node.isRequired,
  icon: PropTypes.elementType,
  accent: PropTypes.shape({ tint: PropTypes.string, ink: PropTypes.string }).isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      primary: PropTypes.node,
      secondary: PropTypes.node,
      amount: PropTypes.node,
      status: PropTypes.string,
      to: PropTypes.string,
    })
  ),
  loading: PropTypes.bool,
  emptyText: PropTypes.string,
  footer: PropTypes.node,
  action: PropTypes.node,
};

RelatedList.defaultProps = { icon: null, rows: [], loading: false, emptyText: "Nothing here yet.", footer: null, action: null };
